import { useEffect, useState } from 'react';
import { Banner, Button, Dialog, RadioCard, Textarea } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import type { ReservationItem } from './api';
import styles from './ReservationApprovalPanel.module.css';

export interface RejectReservationDialogProps {
  open: boolean;
  reservation: ReservationItem;
  /** The note typed into the panel's note field, taken over as the start of the mandatory note. */
  initialNote: string;
  /** `reservation`: Reservierung 09 (reason radios of the API) · `release`: the request's rejection (free reason). */
  kind: 'reservation' | 'release';
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (body: { reason: string; note: string }) => void;
}

const REASON_REQUIRED = 'Bitte wählen Sie einen Grund.';
const FREE_REASON_REQUIRED = 'Bitte geben Sie einen Grund an.';
const NOTE_REQUIRED = 'Bitte geben Sie eine Notiz an den Betreuer an.';

/** Reservierung 09 „Reservierung ablehnen": mandatory reason, mandatory note, the two consequences. */
export function RejectReservationDialog({
  open,
  reservation,
  initialNote,
  kind,
  pending,
  error,
  onClose,
  onSubmit,
}: RejectReservationDialogProps) {
  const [reason, setReason] = useState<string | null>(null);
  const [freeReason, setFreeReason] = useState('');
  const [note, setNote] = useState(initialNote);
  const [errors, setErrors] = useState<{ reason?: string | undefined; note?: string | undefined }>({});

  useEffect(() => {
    if (!open) return;
    setReason(null);
    setFreeReason('');
    setNote(initialNote);
    setErrors({});
  }, [open, initialNote]);

  const advisor = reservation.advisor?.name ?? reservation.startedBy.name;
  const serverFields = error instanceof ApiError ? error.fields : {};

  function submit() {
    const next: typeof errors = {};
    const chosen = kind === 'reservation' ? reason : freeReason.trim() || null;
    if (chosen === null) next.reason = kind === 'reservation' ? REASON_REQUIRED : FREE_REASON_REQUIRED;
    if (kind === 'reservation' && note.trim() === '') next.note = NOTE_REQUIRED;
    setErrors(next);
    if (chosen === null || Object.keys(next).length > 0) return;
    onSubmit({ reason: chosen, note: note.trim() });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={540}
      eyebrow={kind === 'reservation' ? 'Reservierung ablehnen' : 'Aufhebung ablehnen'}
      title={`${reservation.unit.number} · ${reservation.contact.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={pending} onClick={submit}>
            Ablehnen
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {error !== null && error !== undefined && Object.keys(serverFields).length === 0 ? (
          <Banner tone="danger">{errorMessage(error)}</Banner>
        ) : null}
        {kind === 'reservation' ? (
          <>
            <div className={styles.fieldLabel}>
              Grund <span className={styles.required}>*</span>
            </div>
            <div className={styles.reasons} role="radiogroup" aria-label="Grund">
              {reservation.options.rejectionReasons.map((option) => (
                <RadioCard
                  key={option}
                  name="rejection-reason"
                  value={option}
                  checked={reason === option}
                  onChange={() => {
                    setReason(option);
                    setErrors((current) => ({ ...current, reason: undefined }));
                  }}
                  title={option}
                />
              ))}
            </div>
            {(errors.reason ?? serverFields['reason']) ? (
              <div className={styles.fieldError} role="alert">
                {errors.reason ?? serverFields['reason']}
              </div>
            ) : null}
          </>
        ) : (
          <Textarea
            label="Grund"
            required
            rows={2}
            value={freeReason}
            onChange={(event) => setFreeReason(event.target.value)}
            error={errors.reason ?? serverFields['reason']}
          />
        )}
        <Textarea
          label={`Notiz an ${advisor}`}
          required={kind === 'reservation'}
          optional={kind === 'release'}
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          error={errors.note ?? serverFields['note']}
        />
        <div className={styles.notes}>
          {kind === 'reservation' ? (
            <>
              <span>{reservation.unit.number} wird wieder frei.</span>
              <span>
                {reservation.contact.name} erhält keine automatische Nachricht. {advisor} bekommt eine Task,
                den Kunden zu informieren.
              </span>
            </>
          ) : (
            <>
              <span>Die Reservierung {reservation.unit.number} bleibt bestehen.</span>
              <span>Der Antrag wird abgelehnt; die antragstellende Person wird benachrichtigt.</span>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
